import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { EmailTemplatesEditDirective } from 'src/app/class/email-templates-edit';
import { EmailTrigger } from 'src/app/enum/email.trigger';
import { Event } from 'src/app/model/event';
import { User } from 'src/app/model/user';
import { EventsService } from 'src/app/service/events.service';
import { NotificationService } from 'src/app/service/notification.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { PREVIEW_MARK_OPEN, PREVIEW_MARK_CLOSE } from '../variable-template-editor/variable-template-editor.component';

export interface EmailTemplateEditDialogData {
  event: Event;
  trigger: EmailTrigger | string;
  triggerLabel: string;
  // Modo "template padrão" (admin global): SEM evento. Carrega/salva o template-base
  // do gatilho (byTrigger, não byEvent) e oculta os destinatários (que são por evento).
  defaultMode?: boolean;
}

// Editor de template de e-mail em janela modal. Reusa o EmailTemplatesEditDirective
// (form, preview, validação de variáveis, fallback p/ template padrão), espelhando o
// editor inline de event-settings-email, mas com estado/`#search` PRÓPRIOS (sem colidir
// com o editor inline que segue na página). Saída controlada: só "Salvar" (persiste) ou
// "Descartar" (confirma se houver edição não salva); ESC/backdrop bloqueados (disableClose).
//
// NOTA (redundância consciente): os destinatários ("enviar cópia para") aqui são
// autocontidos — add/remove operam sobre `notificationEmail.to` + salvam o template.
// O editor inline acopla o delete à matriz de co-chairs; a reconciliação dessa
// redundância fica para depois (decisão do usuário).
@Component({
  selector: 'app-email-template-edit-dialog',
  templateUrl: './email-template-edit-dialog.component.html',
  styleUrls: ['./email-template-edit-dialog.component.scss']
})
export class EmailTemplateEditDialogComponent extends EmailTemplatesEditDirective implements OnInit, OnDestroy {
  event: Event;
  emailExists = false;
  // Enquanto o template não chega do backend, o modal mostra um indicador de
  // "trabalhando" no lugar do editor (evita campos vazios piscando).
  loading = true;
  // Durante o PUT/POST do "Salvar": desabilita os botões e mostra "Salvando…".
  // O modal só fecha após o 200; no erro, volta a false e segue aberto p/ correção.
  saving = false;

  // Mudança de destinatários (add/remove) é local até "Salvar"; este flag faz o
  // descarte/fechamento pedir confirmação mesmo quando só os destinatários mudaram
  // (o filterForm.dirty não detecta isso).
  recipientsDirty = false;

  // Busca de destinatário em overlay flutuante (aberta pelo chip "+").
  recipientSearchOpen = false;
  recipientSearchPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 }
  ];

  // Form (somente leitura) que alimenta os campos-espelho do preview à direita —
  // mesmos componentes da esquerda, com os valores já com variáveis substituídas.
  previewForm: FormGroup;

  private livePreviewSub: Subscription;
  // Editores gêmeos (editor | prévia) p/ sincronizar a altura: cada par cresce e
  // encolhe junto (a altura do par = a do conteúdo mais alto dos dois).
  private subjectEditorEl: HTMLElement;
  private subjectPreviewEl: HTMLElement;
  private contentEditorEl: HTMLElement;
  private contentPreviewEl: HTMLElement;

  // Controls do form de edição expostos como FormControl p/ o app-variable-template-editor.
  get subjectControl(): FormControl { return this.filterForm.get('emailSubject') as FormControl; }
  get contentControl(): FormControl { return this.filterForm.get('emailTextForm') as FormControl; }
  // Controls do preview (read-only, texto substituído) — mesmo componente da esquerda.
  get previewSubjectControl(): FormControl { return this.previewForm.get('previewSubject') as FormControl; }
  get previewContentControl(): FormControl { return this.previewForm.get('previewContent') as FormControl; }

  constructor(
    protected fb: FormBuilder,
    protected translateService: TranslateService,
    protected eventsService: EventsService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<EmailTemplateEditDialogComponent>,
    private elementRef: ElementRef<HTMLElement>,
    @Inject(MAT_DIALOG_DATA) public data: EmailTemplateEditDialogData
  ) {
    super(true, fb, translateService, eventsService);
  }

  ngOnInit(): void {
    this.event = this.data.event;
    this.initComponent();
  }

  initComponent(): void {
    this.userForm = this.fb.group({ user: ['', [Validators.required]] });
    this.previewForm = this.fb.group({ previewSubject: [''], previewContent: [''] });
    this.initForm();
    this.filterForm.get('emailTrigger').setValue(this.data.trigger);
    this.getEmailTemplate(this.data.trigger as EmailTrigger);

    // Preview AO VIVO: a cada edição de título/conteúdo, recomputa a prévia (com
    // variáveis substituídas) e joga nos campos-espelho (previewForm, read-only).
    // debounce curto agrupa teclas sem perder a sensação de "imediato".
    this.livePreviewSub = this.filterForm.valueChanges
      .pipe(debounceTime(80))
      .subscribe(() => {
        this.updatePreview();
        // Após a prévia atualizar (próximo tick), iguala a altura dos pares gêmeos.
        setTimeout(() => this.syncTwinHeights());
      });
  }

  // Substitui as variáveis do template e escreve TEXTO PLANO nos campos-espelho.
  // Resolve ONLINE (sem backend): cada `{{a.b.c}}` é resolvido por CAMINHO contra
  // um contexto de exemplo onde `event` é REAL (valores verdadeiros) e as demais
  // entidades (submission, user, review…) são exemplos ilustrativos. Fallback:
  // catálogo atual (variable-defaults, estilo underscore) p/ o que ainda casar.
  // O que não resolver fica literal. (Refinar exemplos quando vier o catálogo do
  // backend — ver memória project_email_var_catalog_mismatch.)
  private updatePreview(): void {
    if (!this.previewForm) { return; }
    const subject = this.filterForm.value.emailSubject || '';
    const content = this.filterForm.value.emailTextForm || '';
    const variables = (this.emailTriggerVariables || []).map(v => <string>v.id);

    const applyWith = (catalog: { [key: string]: string }) => {
      const ctx = this.buildPreviewContext();
      // Marca cada valor SUBSTITUÍDO com as sentinelas → o editor de preview o
      // renderiza com destaque azul (.vte-preview-var). Não-resolvidos ficam literais.
      const mark = (v: string) => PREVIEW_MARK_OPEN + v + PREVIEW_MARK_CLOSE;
      const flexRegex = /\{\{\s*([^{}]+?)\s*\}\}/g;
      const substitute = (text: string) => text.replace(flexRegex, (matched, inner) => {
        const path = (inner as string).trim();
        const fromCtx = this.resolvePath(ctx, path);
        if (fromCtx !== undefined && fromCtx !== null && typeof fromCtx !== 'object') {
          return mark(String(fromCtx));
        }
        const fromCatalog = catalog[`{{${path}}}`];
        return fromCatalog !== undefined ? mark(fromCatalog) : matched;
      });
      this.previewForm.patchValue({
        previewSubject: substitute(subject),
        previewContent: substitute(content)
      });
    };

    if (!variables.length) { applyWith({}); return; }

    this.translateService.get(variables.map(v => `admin.email-template.variable-defaults.${v}`)).subscribe(defaults => {
      const catalog: { [key: string]: string } = {};
      Object.keys(defaults).forEach(k => {
        const key = k.split('variable-defaults.')[1];
        this.translateService.get(`admin.email-template.variable-defaults.${key}`,
          { domain: window.location.origin, event: this.event })
          .subscribe(item => { catalog[key] = item; });
      });
      applyWith(catalog);
    });
  }

  // Contexto de exemplo p/ resolver variáveis no preview SEM backend. `event` é
  // REAL; o resto é exemplo ilustrativo (placeholder de preview). Cobre os nomes
  // em ponto usados nos templates reais; nomes não cobertos ficam literais.
  private buildPreviewContext(): any {
    const origin = window.location.origin;
    const ev: any = this.event || {};
    return {
      event: this.event,
      domain: origin,
      username: 'Ana Pereira',
      confirmationLink: `${origin}/confirm/exemplo/1234/`,
      user: { title: 'Dra.', full_name: 'Ana Pereira', first_name: 'Ana', last_name: 'Pereira', email: 'ana@exemplo.com' },
      submission: { id: '70654', title: 'Exemplo de título de submissão', abstract: 'Exemplo de resumo da submissão.' },
      review: { id: '310321', due_at: '11 nov. 2025, 23:59 UTC', assigned_by: 'Maria Silva', assigned_by_email: 'maria@exemplo.com' },
      reviewer: { full_name: 'João Souza', email: 'joao@exemplo.com' },
      track: { name: 'Trilha de Exemplo' },
      link: {
        conference_page: `${origin}/${ev.uri || 'evento'}/`,
        submission_page: `${origin}/submissions/1234/`,
        reviews_page: `${origin}/reviews/`
      }
    };
  }

  // Resolve um caminho com ponto (ex.: 'event.acronym' ou 'username') no contexto.
  private resolvePath(ctx: any, path: string): any {
    return path.split('.').reduce((obj, key) => (obj == null ? undefined : obj[key]), ctx);
  }

  // A sincronização de altura dos gêmeos é feita em finishLoading() (após `loading`
  // virar false e os editores, escondidos por *ngIf no load, existirem no DOM).

  // Conclui o carregamento: revela o editor e captura/sincroniza os pares gêmeos.
  private finishLoading(): void {
    this.loading = false;
    setTimeout(() => this.setupTwins());
  }

  ngOnDestroy(): void {
    this.livePreviewSub?.unsubscribe();
  }

  // Janela redimensionou → a largura do editor muda (quebra de linha) → re-sincroniza.
  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncTwinHeights();
  }

  // Captura os 4 editores (título/conteúdo × editor/prévia) e faz a 1ª sincronização.
  private setupTwins(): void {
    const host = this.elementRef.nativeElement;
    this.subjectEditorEl = host.querySelector('.edit-pane .vte-editor:not(.vte-multiline)') as HTMLElement;
    this.subjectPreviewEl = host.querySelector('.preview-pane .vte-editor:not(.vte-multiline)') as HTMLElement;
    this.contentEditorEl = host.querySelector('.edit-pane .vte-editor.vte-multiline') as HTMLElement;
    this.contentPreviewEl = host.querySelector('.preview-pane .vte-editor.vte-multiline') as HTMLElement;
    this.syncTwinHeights();
  }

  // Iguala a altura de cada par gêmeo (editor | prévia) ao maior dos dois.
  private syncTwinHeights(): void {
    this.syncPair(this.subjectEditorEl, this.subjectPreviewEl, 40);
    this.syncPair(this.contentEditorEl, this.contentPreviewEl, 200);
  }

  // Reseta o min-height ao PISO (p/ medir a altura natural do conteúdo — permite
  // CONTRAÇÃO), mede os dois e aplica o MÁXIMO nos dois. O reset + medição + set
  // acontecem num tick só (reflow síncrono, sem paint no meio) → sem flicker.
  private syncPair(a: HTMLElement, b: HTMLElement, floor: number): void {
    if (!a || !b) { return; }
    a.style.minHeight = `${floor}px`;
    b.style.minHeight = `${floor}px`;
    const h = Math.max(a.scrollHeight, b.scrollHeight);
    a.style.minHeight = `${h}px`;
    b.style.minHeight = `${h}px`;
  }

  getSelectedTriggerLabel(): string {
    return this.data.triggerLabel;
  }

  // Carrega o template do evento p/ o gatilho; fallback p/ o template padrão (404).
  // Migra o conteúdo do ESQUEMA ANTIGO (ponto/alias) p/ o catálogo atual (underscore)
  // — templates salvos antes da migração de variáveis do backend (ex.: este, salvo em
  // 2024) seriam rejeitados com `content: invalid` em qualquer gravação. Ver
  // memória project_email_var_catalog_mismatch.
  getEmailTemplate(trigger: EmailTrigger): void {
    // Modo padrão (admin global): o template-base vem por gatilho (sem evento).
    if (this.data.defaultMode) {
      this.getDefaultEmailTemplate(trigger);
      return;
    }
    this.eventsService.getEventNotificationEmailbyEvent(trigger, this.event.id)
      .subscribe(nf => {
        this.changeType = true;
        nf.subject = this.migrateLegacyVariables(nf.subject);
        nf.content = this.migrateLegacyVariables(nf.content);
        this.notificationEmail = nf;
        this.emailExists = true;
        this.filterForm.patchValue({ emailSubject: nf.subject, emailTextForm: nf.content });
        this.finishLoading();
      }, () => {
        this.getDefaultEmailTemplate(trigger);
      });
  }

  // Fallback (404): template padrão do gatilho. Sobrescreve o da diretiva p/ também
  // migrar variáveis legadas (idempotente) e encerrar o estado de carregamento.
  getDefaultEmailTemplate(trigger: EmailTrigger): void {
    this.eventsService.getEventNotificationEmail(trigger).subscribe(nf => {
      this.changeType = true;
      this.emailExists = false;
      nf.subject = this.migrateLegacyVariables(nf.subject);
      nf.content = this.migrateLegacyVariables(nf.content);
      this.notificationEmail = nf;
      this.filterForm.patchValue({ emailSubject: nf.subject, emailTextForm: nf.content });
      this.finishLoading();
    }, () => this.finishLoading());
  }

  // Mapa de migração: variável do esquema ANTIGO (ponto ou alias) → atual (underscore).
  private static readonly LEGACY_VARIABLE_MAP: { [legacy: string]: string } = {
    'event.acronym': 'event_acronym', 'event.year': 'event_year', 'event.name': 'event_name',
    'event.end_at': 'event_end_at',
    'submission.id': 'submission_id', 'submission.title': 'submission_title',
    'submission.abstract': 'submission_abstract',
    'user.title': 'user_title', 'user.full_name': 'user_full_name',
    'user.first_name': 'user_first_name', 'user.last_name': 'user_last_name', 'user.email': 'user_email',
    'username': 'user_full_name',
    'review.id': 'review_id', 'review.due_at': 'review_due_at',
    'reviewer.full_name': 'reviewer_user_full_name', 'reviewer.email': 'reviewer_email',
    'track.name': 'track_name',
    'discussion_message.content': 'discussion_message_content',
    'discussion_message.created_at': 'discussion_message_date',
    'discussion_message_created_at': 'discussion_message_date',
  };

  // Converte um template do esquema antigo p/ o atual. Idempotente: variáveis já no
  // formato novo (não presentes no mapa) ficam intactas. Faz primeiro as substituições
  // de FRASE (URLs montadas com {{domain}}, que viram uma variável de link única) e
  // depois a troca token a token.
  private migrateLegacyVariables(text: string): string {
    if (!text) { return text; }
    // Frase: link de submissão montado manualmente → variável de link única.
    let out = text.replace(
      /https?:\/\/\{\{\s*domain\s*\}\}submissions\/\{\{\s*submission\.id\s*\}\}\/?/g,
      '{{link_submission_page}}'
    );
    // Token a token: {{ legado }} → {{ atual }} (preserva os não mapeados).
    out = out.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (matched, inner) => {
      const mapped = EmailTemplateEditDialogComponent.LEGACY_VARIABLE_MAP[(inner as string).trim()];
      return mapped ? `{{${mapped}}}` : matched;
    });
    return out;
  }

  updateSelectedEmailTemplate(): void {
    if (this.emailExists) {
      this.eventsService.editEventNotificationEmail(this.event.id, this.notificationEmail.trigger, this.notificationEmail)
        .subscribe(nf => {
          this.notificationService.notify('admin.email-template.email-updated');
          this.notificationEmail = nf;
        });
    } else {
      this.eventsService.createEventNotificationEmail(this.event.id, this.notificationEmail)
        .subscribe(nf => {
          this.notificationEmail = nf;
          this.emailExists = true;
        });
    }
  }

  // Abre/fecha a busca flutuante.
  openRecipientSearch(): void {
    this.recipientSearchOpen = !this.recipientSearchOpen;
  }

  // Foca o input da busca quando o overlay anexa (cursor pronto pra digitar).
  focusRecipientInput(): void {
    setTimeout(() => {
      const input = document.querySelector('.recipient-search-popover input') as HTMLInputElement;
      if (input) { input.focus(); }
    });
  }

  // Disparado pelo (addedUser) do app-user-search: selecionar (clique OU Enter) já
  // adiciona o destinatário — sem botão de confirmação. SÓ em memória (persiste no
  // "Salvar"). Guarda o objeto User inteiro p/ o chip exibir `user.name` na hora; a
  // conversão p/ id acontece no save() (via eventNotificationEmail). Evita duplicado.
  onUserPicked(picked: User): void {
    if (!picked) { return; }
    if (!this.notificationEmail.to) { this.notificationEmail.to = []; }
    if (!this.notificationEmail.to.some((u: User) => u.id === picked.id)) {
      this.notificationEmail.to.push(picked);
      this.recipientsDirty = true;
    }
    // Limpa a seleção interna da busca p/ a próxima abertura começar vazia e fecha.
    this.userForm.get('user').setValue([]);
    this.recipientSearchOpen = false;
  }

  // Remove destinatário SÓ em memória (persiste no "Salvar").
  deleteUser(user: User): void {
    this.notificationEmail.to = (this.notificationEmail.to || []).filter((u: User) => u.id !== user.id);
    this.recipientsDirty = true;
  }

  // Salvar: valida variáveis, persiste título/conteúdo + destinatários e SÓ fecha
  // após o 200. No erro (ex.: 400), o modal continua aberto p/ correção — o
  // interceptor de HTTP já exibe o motivo do backend, então não notificamos de novo.
  save(): void {
    if (this.saving) { return; }
    const invalidKeys = this.validateTemplateVariables();
    if (invalidKeys.length > 0) {
      this.notificationService.notifyError('admin.email-template.invalid-variables', { params: { variables: invalidKeys.join(', ') } });
      return;
    }

    // Monta o payload SEM mutar this.notificationEmail: mantém `to` como objetos User
    // p/ os chips seguirem com nome caso o save falhe e o modal continue aberto.
    const payload = {
      ...this.notificationEmail,
      subject: this.filterForm.value.emailSubject,
      content: this.filterForm.value.emailTextForm,
      event: null,
      trigger: this.filterForm.value.emailTrigger,
      // Destinatários são por evento; no modo padrão preserva o que veio do backend.
      to: this.data.defaultMode
        ? this.notificationEmail.to
        : (this.notificationEmail.to || []).map((u: User) => u.id)
    };
    let request;
    if (this.data.defaultMode) {
      // Template-base: sempre PUT por gatilho (não há create/evento).
      request = this.eventsService.editDefaultEmailTemplate(payload.trigger, payload);
    } else {
      request = this.emailExists
        ? this.eventsService.editEventNotificationEmail(this.event.id, payload.trigger, payload)
        : this.eventsService.createEventNotificationEmail(this.event.id, payload);
    }

    this.saving = true;
    request.subscribe(() => {
      this.notificationService.notify('admin.email-template.email-updated');
      this.dialogRef.close('saved');
    }, () => {
      this.saving = false;
    });
  }

  // Descartar: se houver edição não salva (conteúdo OU destinatários), confirma
  // antes de fechar.
  discard(): void {
    if (this.filterForm.dirty || this.recipientsDirty) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: 'admin.email-template.discard-dialog.title',
          content: 'admin.email-template.discard-dialog.content',
          cancel: 'forms.buttons.cancel',
          confirm: 'forms.buttons.discard',
          destructive: true
        }
      }).afterClosed().subscribe(confirmed => {
        if (confirmed) { this.dialogRef.close('discarded'); }
      });
    } else {
      this.dialogRef.close('discarded');
    }
  }
}
